import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Menu, X, Shield, Home as HomeIcon, Info, Phone,
  Sun, Moon, ChevronRight, Mail, MapPin, Facebook,
  Twitter, Instagram, Linkedin
} from 'lucide-react';
import logo from './assets/logo.png';
import Home from './pages/Home';
import Services from './pages/Services';
import About from './pages/About';
import Contact from './pages/Contact';

// --- Components ---

const PrivacyPolicy = () => <div className="container mx-auto px-4 py-20"><h1>Privacy Policy</h1><p>Our privacy policy details.</p></div>;
const Terms = () => <div className="container mx-auto px-4 py-20"><h1>Terms & Conditions</h1><p>Terms and conditions of our services.</p></div>;

const NavLink = ({ to, icon: Icon, label, onClick }: { to: string, icon: any, label: string, onClick?: () => void }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 group
        ${isActive
          ? 'bg-primary-500/10 text-primary-600 dark:text-primary-400 font-medium'
          : 'text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-800'
        }`}
    >
      <Icon size={18} className={`transition-colors ${isActive ? 'text-primary-600 dark:text-primary-400' : 'text-secondary-400 group-hover:text-primary-500'}`} />
      <span>{label}</span>
    </Link>
  );
};

const ThemeToggle = ({ darkMode, setDarkMode }: { darkMode: boolean, setDarkMode: (d: boolean) => void }) => (
  <button
    onClick={() => setDarkMode(!darkMode)}
    className="p-2 rounded-full bg-secondary-100 dark:bg-secondary-800 text-secondary-600 dark:text-secondary-300 hover:text-primary-500 transition-colors"
    aria-label="Toggle Theme"
  >
    {darkMode ? <Sun size={20} /> : <Moon size={20} />}
  </button>
);

const Footer = () => (
  <footer className="bg-secondary-50 dark:bg-secondary-900 border-t border-secondary-200 dark:border-secondary-800 pt-16 pb-8">
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Shield className="text-primary-600 w-8 h-8" />
            <span className="font-bold text-xl tracking-tight text-secondary-900 dark:text-white">Devdoot Security</span>
          </div>
          <p className="text-secondary-500 dark:text-secondary-400 leading-relaxed">
            Providing elite security solutions with intelligence and precision. Trust us to protect what matters most to you.
          </p>
          <div className="flex gap-4">
            {[Facebook, Twitter, Instagram, Linkedin].map((Icon, i) => (
              <a key={i} href="#" className="p-2 rounded-full bg-white dark:bg-secondary-800 text-secondary-400 hover:text-primary-500 hover:shadow-md transition-all">
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-bold text-lg mb-6 text-secondary-900 dark:text-white">Quick Links</h3>
          <ul className="space-y-3">
            {[
              { label: 'Home', to: '/' },
              { label: 'Services', to: '/services' },
              { label: 'About Us', to: '/about' },
              { label: 'Contact', to: '/contact' },
            ].map((link) => (
              <li key={link.to}>
                <Link to={link.to} className="text-secondary-500 dark:text-secondary-400 hover:text-primary-500 flex items-center gap-2 transition-colors">
                  <ChevronRight size={14} /> {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-bold text-lg mb-6 text-secondary-900 dark:text-white">Services</h3>
          <ul className="space-y-3">
            {['Manned Guarding', 'Electronic Security', 'Event Security', 'Risk Analysis'].map((item) => (
              <li key={item} className="text-secondary-500 dark:text-secondary-400 hover:text-primary-500 cursor-pointer flex items-center gap-2 transition-colors">
                <ChevronRight size={14} /> {item}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-bold text-lg mb-6 text-secondary-900 dark:text-white">Contact Us</h3>
          <ul className="space-y-4">
            <li className="flex items-start gap-3 text-secondary-500 dark:text-secondary-400">
              <MapPin size={20} className="text-primary-500 shrink-0 mt-1" />
              <span>Near main Hanuman nagar, Post office gali ke bagal gali main road no=2, dwarkapuri. Patna, Bihar 800020</span>
            </li>
            <li className="flex items-center gap-3 text-secondary-500 dark:text-secondary-400">
              <Phone size={20} className="text-primary-500 shrink-0" />
              <span>+91 9852891115</span>
            </li>
            <li className="flex items-center gap-3 text-secondary-500 dark:text-secondary-400">
              <Mail size={20} className="text-primary-500 shrink-0" />
              <span>info@devdootsecurity.com</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-secondary-200 dark:border-secondary-800 pt-8 text-center text-secondary-400 text-sm">
        <p>&copy; {new Date().getFullYear()} Devdoot Intelligence & Securities Pvt Ltd. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

const BottomNav = ({ darkMode, setDarkMode }: { darkMode: boolean, setDarkMode: (d: boolean) => void }) => {
  const location = useLocation();

  const NavItem = ({ to, icon: Icon, label }: { to: string, icon: any, label: string }) => {
    const isActive = location.pathname === to;
    return (
      <Link
        to={to}
        className={`flex flex-col items-center justify-center w-full py-2 transition-colors duration-300
          ${isActive ? 'text-primary-600 dark:text-primary-400' : 'text-secondary-500 dark:text-secondary-400 hover:text-primary-500'}
        `}
      >
        <Icon size={24} className={`mb-1 transition-transform ${isActive ? 'scale-110' : ''}`} />
        <span className="text-[10px] font-medium tracking-wide">{label}</span>
      </Link>
    );
  };

  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-secondary-900 border-t border-secondary-200 dark:border-secondary-800 pb-safe z-50">
      <div className="flex items-center justify-around px-2 pb-1 pt-1">
        <NavItem to="/" icon={HomeIcon} label="Home" />
        <NavItem to="/services" icon={Shield} label="Services" />
        <div className="relative -top-5">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="w-14 h-14 rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
          >
            {darkMode ? <Sun size={24} /> : <Moon size={24} />}
          </button>
        </div>
        <NavItem to="/about" icon={Info} label="About" />
        <NavItem to="/contact" icon={Phone} label="Contact" />
      </div>
    </div>
  );
};

const App = () => {
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved) setDarkMode(saved === 'true');
    else if (window.matchMedia('(prefers-color-scheme: dark)').matches) setDarkMode(true);
  }, []);

  useEffect(() => {
    localStorage.setItem('darkMode', darkMode.toString());
    if (darkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [darkMode]);

  return (
    <Router>
      <div className={`min-h-screen flex flex-col bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white font-sans selection:bg-primary-500/30 selection:text-primary-700 pb-16 md:pb-0`}>
        {/* Navigation */}
        <nav className="glass-nav">
          <div className="container mx-auto px-4 h-20 flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-primary-500 blur-lg opacity-20 group-hover:opacity-40 transition-opacity rounded-full"></div>
                <img src={logo} alt="Logo" className="h-10 relative z-10" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-lg leading-tight tracking-tight">Devdoot</span>
                <span className="text-xs text-secondary-500 font-medium tracking-wider">INTELLIGENCE & SECURITY</span>
              </div>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-2">
              <NavLink to="/" icon={HomeIcon} label="Home" />
              <NavLink to="/services" icon={Shield} label="Services" />
              <NavLink to="/about" icon={Info} label="About" />
              <NavLink to="/contact" icon={Phone} label="Contact" />
              <div className="w-px h-6 bg-secondary-200 dark:bg-secondary-700 mx-2"></div>
              <ThemeToggle darkMode={darkMode} setDarkMode={setDarkMode} />
            </div>

            {/* Mobile Header (Just Logo) */}
            <div className="md:hidden">
              {/* Logo is already visible above */}
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-grow pt-4">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
          </Routes>
        </main>

        <Footer />
        <BottomNav darkMode={darkMode} setDarkMode={setDarkMode} />
      </div>
    </Router>
  );
};

export default App;
